import React, { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { UserRole, UserRoleRequest } from "model/Company";
import type {
  CompanyUser,
  CompanyUserRequestDto,
  StampClockUserRequestDto,
} from "model/Company";
import { useAlertStore } from "stores/alertStore/alertStore";
import { encryptPassword } from "utils/encrypt";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { validateEmail } from "web-apps/company/utils/utils";
import {
  isStampClockEmailValid,
  isStampClockPasswordValid,
  normalizeStampClockCredentials,
} from "web-apps/company/components/companySettingsV2/companyStampClock/stampClock.create.validator";
import {
  CompanyUserFormActions,
  CompanyUserFormFields,
  HiddenRadio,
  RadioControl,
  RadioGroup,
  RadioLabel,
  RadioOption,
} from "./companyUsers.styled";

type StandardProps = {
  readonly currentUser: CompanyUser | null;
  readonly mode?: "standard";
  readonly onSubmittingChange?: (isSubmitting: boolean) => void;
  readonly setCurrentUser: React.Dispatch<
    React.SetStateAction<CompanyUser | null>
  >;
  readonly setShowAddUserModal: React.Dispatch<React.SetStateAction<boolean>>;
};

type StampClockProps = {
  readonly mode: "stampClock";
  readonly onCancel: () => void;
  readonly onSubmittingChange?: (isSubmitting: boolean) => void;
} & (
  | {
      readonly currentUser?: never;
      readonly onCreated: (email: string) => void;
    }
  | {
      readonly currentUser: CompanyUser;
      readonly onUpdated: () => void;
    }
);

type Props = StandardProps | StampClockProps;

type RoleOption = {
  readonly label: string;
  readonly value: UserRoleRequest;
};

const getDefaultRole = (currentUser: CompanyUser | null): UserRoleRequest => {
  switch (currentUser?.role) {
    case UserRole.COMPANY_OWNER:
      return UserRoleRequest.COMPANY_OWNER;
    case UserRole.COMPANY_TIMESTAMP:
      return UserRoleRequest.COMPANY_TIMESTAMP;
    case UserRole.COMPANY_ADMIN:
    default:
      return UserRoleRequest.COMPANY_ADMIN;
  }
};

export const CompanyUserForm: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [companyState, companyActions] = useCompanyStore();
  const isStampClockMode = props.mode === "stampClock";
  const currentUser = props.currentUser ?? null;
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState(
    currentUser?.role === UserRole.COMPANY_TIMESTAMP
      ? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(" ")
      : (currentUser?.firstName ?? ""),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [lastName, setLastName] = useState(currentUser?.lastName ?? "");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [role, setRole] = useState<UserRoleRequest>(
    isStampClockMode
      ? UserRoleRequest.COMPANY_TIMESTAMP
      : getDefaultRole(currentUser),
  );
  const isStampClockUser = role === UserRoleRequest.COMPANY_TIMESTAMP;
  const fieldLabel = (key: string): string =>
    isStampClockUser
      ? `${t(key)} (${t("CompanyStampClock.Mandatory")})`
      : t(key);
  const hasStampClockModule =
    companyState.companySettings?.find(
      (setting: { key: string; value: string }) =>
        setting.key === "CompanyShowStampclockModule",
    )?.value === "true";
  const isEmailValid = isStampClockUser
    ? isStampClockEmailValid(email)
    : validateEmail(email);
  const isEmailErrorVisible = Boolean(email) && !isEmailValid;

  useEffect(() => {
    setRole(
      isStampClockMode
        ? UserRoleRequest.COMPANY_TIMESTAMP
        : getDefaultRole(currentUser),
    );
  }, [currentUser, isStampClockMode]);

  const roleOptions: RoleOption[] = [
    {
      label: t("CompanyUsers.CompanyOwner"),
      value: UserRoleRequest.COMPANY_OWNER,
    },
    {
      label: t("CompanyUsers.CompanyAdmin"),
      value: UserRoleRequest.COMPANY_ADMIN,
    },
  ];

  if (hasStampClockModule) {
    roleOptions.push({
      label: t("CompanyUsers.CompanyStampClock"),
      value: UserRoleRequest.COMPANY_TIMESTAMP,
    });
  }

  const isFormValid = (): boolean => {
    if (isStampClockUser) {
      return (
        Boolean(firstName.trim()) &&
        (Boolean(currentUser) ||
          (isEmailValid && isStampClockPasswordValid(password)))
      );
    }
    if (currentUser) {
      return Boolean(firstName && lastName);
    }
    return Boolean(firstName && lastName && email && isEmailValid && role);
  };

  const setSubmitting = (isSubmitting: boolean): void => {
    setIsLoading(isSubmitting);
    props.onSubmittingChange?.(isSubmitting);
  };

  const createStampClockUser = async (companyId: number): Promise<string> => {
    const credentials = normalizeStampClockCredentials({ email, password });
    const data: StampClockUserRequestDto = {
      email: credentials.email,
      firstName: firstName.trim(),
      provisionToken: await encryptPassword(
        credentials.password,
        credentials.email,
      ),
      role: UserRoleRequest.COMPANY_TIMESTAMP,
    };
    await companyActions.addCompanyUser(companyId, data);
    setPassword("");
    return credentials.email;
  };

  const createStandardUserRequest =
    async (): Promise<CompanyUserRequestDto> => {
      const provisionToken =
        role === UserRoleRequest.COMPANY_TIMESTAMP && password !== ""
          ? await encryptPassword(password, email.trim())
          : "";
      return {
        email: isStampClockUser ? email.trim() : email,
        firstName: isStampClockUser ? firstName.trim() : firstName,
        lastName: isStampClockUser ? "" : lastName,
        provisionToken,
        role,
      };
    };

  const submitCompanyUser = async (companyId: number): Promise<void> => {
    if (currentUser) {
      await companyActions.updateCompanyUser(
        companyId,
        currentUser.id,
        currentUser.firebaseId,
        role,
        isStampClockUser ? firstName.trim() : firstName,
        isStampClockUser ? "" : lastName,
      );
    } else {
      await companyActions.addCompanyUser(
        companyId,
        await createStandardUserRequest(),
      );
    }
  };

  const showSubmissionError = (): void => {
    const messageKey = currentUser
      ? "CompanyUsers.UserCouldNotBeUpdated"
      : isStampClockMode
        ? "CompanyStampClock.CouldNotCreate"
        : "CompanyUsers.CouldNotAddUser";
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        message: t(messageKey),
        title: t("Alerts.OhNo"),
      },
    });
  };

  const completeSubmission = (createdEmail: string | null): void => {
    if (props.mode === "stampClock") {
      if (props.currentUser) {
        props.onUpdated();
      } else if (createdEmail !== null) {
        props.onCreated(createdEmail);
      }
      return;
    }
    if (currentUser) props.setCurrentUser(null);
    props.setShowAddUserModal(false);
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!companyState.company || !isFormValid() || isLoading) return;
    let createdEmail: string | null = null;
    let isSuccessful = false;
    setSubmitting(true);
    try {
      if (isStampClockMode && !currentUser) {
        createdEmail = await createStampClockUser(companyState.company.id);
      } else {
        await submitCompanyUser(companyState.company.id);
      }
      isSuccessful = true;
    } catch {
      showSubmissionError();
    } finally {
      setSubmitting(false);
    }
    if (isSuccessful) completeSubmission(createdEmail);
  };

  const handleCancel = (): void => {
    if (props.mode === "stampClock") {
      props.onCancel();
      return;
    }
    props.setCurrentUser(null);
    props.setShowAddUserModal(false);
  };

  return (
    <CompanyUserFormFields
      aria-label={t(
        currentUser
          ? "CompanyUsers.UpdateUser"
          : isStampClockMode
            ? "CompanyStampClock.CreateTitle"
            : "CompanyUsers.AddUser",
      )}
      onSubmit={handleSubmit}
    >
      {isStampClockUser ? (
        <InputField
          label={fieldLabel("CompanyStampClock.Username")}
          value={firstName}
          onChange={setFirstName}
          placeholder={`${t("CompanyUsers.CompanyStampClock")} ${companyState.company?.companyName ?? ""}`.trim()}
          disabled={isLoading}
          fullWidth
        />
      ) : (
        <>
          <InputField
            label={fieldLabel("CompanyUsers.FirstName")}
            value={firstName}
            onChange={setFirstName}
            fullWidth
          />
          <InputField
            label={fieldLabel("CompanyUsers.LastName")}
            value={lastName}
            onChange={setLastName}
            fullWidth
          />
        </>
      )}
      {!currentUser && (
        <InputField
          label={fieldLabel(
            isStampClockMode
              ? "CompanyStampClock.LoginEmail"
              : "CompanyUsers.Email",
          )}
          value={email}
          type="email"
          placeholder={
            isStampClockMode
              ? t("CompanyStampClock.EmailPlaceholder")
              : undefined
          }
          autoComplete={isStampClockMode ? "email" : undefined}
          disabled={isStampClockMode && isLoading}
          hasError={isEmailErrorVisible}
          errorText={t("CompanyUsers.InvalidEmail")}
          onChange={setEmail}
          fullWidth
        />
      )}
      {!currentUser && role === UserRoleRequest.COMPANY_TIMESTAMP && (
        <InputField
          label={fieldLabel("General.Password")}
          value={password}
          type="password"
          placeholder={isStampClockMode ? "••••••••" : undefined}
          autoComplete={isStampClockMode ? "new-password" : undefined}
          disabled={isStampClockMode && isLoading}
          hasError={isStampClockUser && passwordError}
          errorText={t("RegisterCompany.PasswordMustBe6Characters")}
          onChange={(value) => {
            setPassword(value);
            setPasswordError(false);
          }}
          onBlur={() => {
            if (isStampClockUser) {
              setPasswordError(!isStampClockPasswordValid(password));
            }
          }}
          fullWidth
        />
      )}

      {!isStampClockMode &&
        currentUser?.role !== UserRole.COMPANY_TIMESTAMP && (
          <RadioGroup role="radiogroup" aria-label={t("CompanyUsers.Role")}>
            {roleOptions.map((option) => (
              <RadioOption key={option.value}>
                <HiddenRadio
                  type="radio"
                  name="company-user-role"
                  value={option.value}
                  checked={role === option.value}
                  onChange={() => {
                    if (option.value !== UserRoleRequest.COMPANY_TIMESTAMP) {
                      setPassword("");
                    }
                    setRole(option.value);
                  }}
                />
                <RadioControl />
                <RadioLabel>{option.label}</RadioLabel>
              </RadioOption>
            ))}
          </RadioGroup>
        )}

      <CompanyUserFormActions>
        <SecondaryButton
          label={t("General.Cancel")}
          disabled={isLoading}
          onClick={handleCancel}
        />
        <PrimaryButton
          label={t(
            isStampClockMode && !currentUser
              ? "CompanyStampClock.CreateButton"
              : "CompanyUsers.Save",
          )}
          type="submit"
          disabled={!isFormValid()}
          isLoading={isLoading}
        />
      </CompanyUserFormActions>
    </CompanyUserFormFields>
  );
};
